/**
 * ModalService
 *
 * Centralized service for displaying modal dialogs.
 * Provides promise-based API for confirm and alert dialogs.
 *
 * Features:
 * - Promise-based returns (confirm: boolean, alert: void)
 * - ESC key and backdrop click dismiss
 * - Focus trap within modal
 * - Body scroll lock when modal open
 * - Focus restoration on close
 * - Variant support (default, danger)
 *
 * Usage:
 *   import { modalService } from '../services/ModalService.js';
 *
 *   // Confirm dialog
 *   const confirmed = await modalService.confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     confirmText: 'Delete',
 *     cancelText: 'Cancel',
 *     variant: 'danger'
 *   });
 *
 *   // Alert dialog
 *   await modalService.alert({
 *     title: 'Notice',
 *     message: 'Operation completed.'
 *   });
 */

import { escapeHtml } from '../core/utils.js';

class ModalService {
  constructor() {
    this.activeModal = null;
    this.previousActiveElement = null;
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  /**
   * Show a confirm dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @param {string} [options.confirmText='Confirm'] - Confirm button text
   * @param {string} [options.cancelText='Cancel'] - Cancel button text
   * @param {string} [options.variant='default'] - Visual variant ('default' | 'danger')
   * @returns {Promise<boolean>} True if confirmed, false if cancelled
   */
  confirm(options) {
    return this.show('confirm', options);
  }

  /**
   * Show an alert dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Dialog message
   * @returns {Promise<void>}
   */
  alert(options) {
    return this.show('alert', options);
  }

  /**
   * Internal method to show a modal
   * @param {string} type - Modal type ('confirm' | 'alert')
   * @param {Object} options - Modal options
   * @returns {Promise<boolean|void>}
   */
  show(type, options) {
    return new Promise((resolve) => {
      // Store focus for restoration
      this.previousActiveElement = document.activeElement;

      // Create modal element
      const modal = this.createModal(type, options, resolve);
      this.activeModal = modal;

      // Add to DOM
      document.body.appendChild(modal.element);

      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Add keyboard listener
      document.addEventListener('keydown', this.boundHandleKeydown);

      // Trigger animation on next frame
      requestAnimationFrame(() => {
        modal.element.classList.add('modal--visible');
        // Focus the appropriate button
        const focusTarget = modal.element.querySelector('.modal__cancel') ||
                           modal.element.querySelector('.modal__confirm');
        if (focusTarget) {
          focusTarget.focus();
        }
      });
    });
  }

  /**
   * Create modal DOM element
   * @param {string} type - Modal type
   * @param {Object} options - Modal options
   * @param {Function} resolve - Promise resolve function
   * @returns {Object} Modal object with element and resolve
   */
  createModal(type, options, resolve) {
    const {
      title = type === 'alert' ? 'Notice' : 'Confirm',
      message = '',
      confirmText = type === 'alert' ? 'OK' : 'Confirm',
      cancelText = 'Cancel',
      variant = 'default'
    } = options;

    const element = document.createElement('div');
    element.className = `modal modal--${type}${variant === 'danger' ? ' modal--danger' : ''}`;
    element.setAttribute('role', 'dialog');
    element.setAttribute('aria-modal', 'true');
    element.setAttribute('aria-labelledby', 'modal-title');

    const actionsHtml = type === 'alert'
      ? `<button class="btn btn--primary modal__confirm">${escapeHtml(confirmText)}</button>`
      : `<button class="btn btn--secondary modal__cancel">${escapeHtml(cancelText)}</button>
         <button class="btn btn--primary modal__confirm">${escapeHtml(confirmText)}</button>`;

    element.innerHTML = `
      <div class="modal__backdrop"></div>
      <div class="modal__container">
        <h2 class="modal__title" id="modal-title">${escapeHtml(title)}</h2>
        <p class="modal__message">${escapeHtml(message)}</p>
        <div class="modal__actions">
          ${actionsHtml}
        </div>
      </div>
    `;

    // Event listeners
    const backdrop = element.querySelector('.modal__backdrop');
    const confirmBtn = element.querySelector('.modal__confirm');
    const cancelBtn = element.querySelector('.modal__cancel');

    backdrop.addEventListener('click', () => this.close(false));
    confirmBtn.addEventListener('click', () => this.close(true));
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.close(false));
    }

    // Focus trap
    element.addEventListener('keydown', (e) => this.handleFocusTrap(e, element));

    return { element, resolve, type };
  }

  /**
   * Close the active modal
   * @param {boolean} result - Result to resolve with (true for confirm, false for cancel)
   */
  close(result) {
    if (!this.activeModal) return;

    const { element, resolve, type } = this.activeModal;

    // Remove keyboard listener
    document.removeEventListener('keydown', this.boundHandleKeydown);

    // Fade out
    element.classList.remove('modal--visible');

    // Remove from DOM after transition
    setTimeout(() => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }

      // Restore body scroll
      document.body.style.overflow = '';

      // Restore focus
      if (this.previousActiveElement && this.previousActiveElement.focus) {
        this.previousActiveElement.focus();
      }

      this.activeModal = null;
      this.previousActiveElement = null;

      // Resolve promise
      if (type === 'alert') {
        resolve();
      } else {
        resolve(result);
      }
    }, 150);
  }

  /**
   * Handle keydown events
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(false);
    }
  }

  /**
   * Handle focus trap within modal
   * @param {KeyboardEvent} event
   * @param {HTMLElement} container
   */
  handleFocusTrap(event, container) {
    if (event.key !== 'Tab') return;

    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

}

// Export singleton instance
export const modalService = new ModalService();
